import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { AffiliateDisclosure } from '../components/AffiliateDisclosure';
import { Seo } from '../components/Seo';
import { productSchema, breadcrumb } from '../lib/structuredData';
import { ogImage } from '../lib/structuredData';
import { api } from '../lib/api';
import { formatPrice } from '../lib/currency';
import { useApp } from '../context/AppContext';
import { ExternalLink, ChevronLeft, BadgeCheck, Info } from 'lucide-react';

export default function StoreProduct() {
  const { slug } = useParams();
  const { currency } = useApp();
  const [p, setP] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.storeProduct(slug).then(({ data }) => { setP(data); })
      .catch(() => setNotFound(true));
  }, [slug]);

  const openOffer = (offer) => {
    api.storeTrack('store_click', { slug, partner: offer.partner_id });
    window.open(api.goVerticalUrl('store', offer.id, { surface: 'store_product', source: `/store/${slug}` }), '_blank');
  };

  if (notFound) return <Layout><div className="max-w-2xl mx-auto py-24 text-center"><p className="text-muted-foreground">Product not found.</p><Link to="/store" className="text-accent font-medium">Back to Travel Store</Link></div></Layout>;
  if (!p) return <Layout><div className="max-w-6xl mx-auto px-4 py-10 grid gap-6 md:grid-cols-2"><Skeleton className="h-80 rounded-2xl" /><div><Skeleton className="h-8 w-2/3 mb-3" /><Skeleton className="h-4 w-full mb-2" /><Skeleton className="h-40 w-full rounded-2xl mt-4" /></div></div></Layout>;

  const best = p.best_offer;
  const bestPriceId = p.best_price_offer_id;
  const bestValueId = p.best_value_offer_id;
  const lowestPrice = p.best_price ?? best?.price ?? (p.offers || []).map((o) => o.price).filter((x) => x != null).sort((a, b) => a - b)[0];
  const productDesc = (p.description || `${p.brand ? p.brand + ' — ' : ''}${p.name}. Compare prices across ${p.offers?.length || 'multiple'} trusted partners on FlightDeals.in.`).slice(0, 300);
  const storeJsonLd = [
    productSchema({
      name: p.name,
      description: productDesc,
      image: p.images?.[0],
      path: `/store/${slug}`,
      brand: p.brand,
      price: lowestPrice,
      offerCount: p.offers?.length,
      rating: p.rating,
      reviewCount: p.review_count,
    }),
    breadcrumb([
      { name: 'Home', path: '/' },
      { name: 'Travel Store', path: '/store' },
      { name: p.name },
    ]),
  ];
  return (
    <Layout>
      <Seo title={p.name} description={productDesc} path={`/store/${slug}`}
        image={ogImage(`store/${slug}.png`)} type="product" jsonLd={storeJsonLd} />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Link to="/store" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4" data-testid="store-back"><ChevronLeft className="h-4 w-4" /> Travel Store</Link>
        <div className="grid gap-8 md:grid-cols-2">
          <div>
            <Card className="overflow-hidden rounded-2xl">
              <div className="aspect-square bg-secondary">{p.images?.[0] && <img src={p.images[0]} alt={p.name} className="w-full h-full object-cover" />}</div>
            </Card>
          </div>
          <div>
            <div className="flex items-center gap-2">
              <p className="text-sm text-muted-foreground">{p.brand}</p>
              {p.is_demo && <Badge variant="outline" className="rounded-full bg-amber-500/15 text-amber-700 border-amber-500/30 text-[10px]">DEMO CATALOG</Badge>}
            </div>
            <h1 className="font-display text-2xl font-semibold mt-0.5" data-testid="store-product-name">{p.name}</h1>
            <p className="text-muted-foreground mt-2 text-sm">{p.description}</p>

            <Card className="mt-5 p-4 rounded-2xl" data-testid="store-price-comparison">
              <div className="flex items-center justify-between mb-3">
                <p className="font-semibold text-sm">Compare prices</p>
                <Badge variant="outline" className="rounded-full text-[10px]">{p.offers?.length} partner{p.offers?.length === 1 ? '' : 's'}</Badge>
              </div>
              <div className="space-y-2">
                {p.offers?.map((o) => {
                  const isBest = o.id === bestPriceId;
                  const isValue = o.id === bestValueId && o.id !== bestPriceId;
                  return (
                    <div key={o.id} className={`flex items-center justify-between gap-3 rounded-xl border p-3 ${isBest ? 'border-accent/40 bg-accent/5' : isValue ? 'border-sky-500/40 bg-sky-500/5' : 'border-border'}`} data-testid={`store-offer-${o.partner_id}`}>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-medium text-sm truncate">{o.partner_name}</p>
                          {isBest && <Badge className="rounded-full bg-accent text-accent-foreground text-[10px]" data-testid="badge-best-price"><BadgeCheck className="h-3 w-3 mr-0.5" /> Best price</Badge>}
                          {isValue && <Badge className="rounded-full bg-sky-500/15 text-sky-700 border border-sky-500/30 text-[10px]" data-testid="badge-best-value"><BadgeCheck className="h-3 w-3 mr-0.5" /> Best value</Badge>}
                          {o.verified && <Badge variant="outline" className="rounded-full text-[10px] text-emerald-700 border-emerald-500/30">verified</Badge>}
                          {o.is_demo && <Badge variant="outline" className="rounded-full text-[10px]">demo</Badge>}
                        </div>
                        <p className="text-[11px] text-muted-foreground">{o.availability === 'in_stock' ? 'In stock' : o.availability}</p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-lg font-semibold tabular">{formatPrice(o.price, currency)}</p>
                      </div>
                      <Button onClick={() => openOffer(o)} className="h-9 rounded-xl bg-accent text-accent-foreground hover:bg-accent/90 font-semibold shrink-0" data-testid={`store-view-deal-${o.partner_id}`}>
                        View deal <ExternalLink className="h-3.5 w-3.5 ml-1" />
                      </Button>
                    </div>
                  );
                })}
              </div>
              <p className="mt-3 text-[11px] text-muted-foreground flex items-start gap-1.5"><Info className="h-3.5 w-3.5 mt-0.5" /> Best price across the {p.offers?.length} partner(s) we track — not an exhaustive market comparison.</p>
            </Card>
            <div className="mt-3"><AffiliateDisclosure variant="footer" /></div>
          </div>
        </div>

        {p.specifications && Object.keys(p.specifications).length > 0 && (
          <Card className="mt-8 p-5 rounded-2xl">
            <h3 className="font-semibold mb-3">Specifications</h3>
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {Object.entries(p.specifications).map(([k, v]) => (
                <div key={k} className="flex justify-between rounded-lg border border-border px-3 py-2 text-sm"><span className="text-muted-foreground">{k}</span><span className="font-medium">{v}</span></div>
              ))}
            </div>
          </Card>
        )}
      </div>
    </Layout>
  );
}
